<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { GENDER_OPTIONS, PREF_OPTIONS } from '@enum/common.enum.js'
import { useLearningAuthStore } from '@stores/auth'
import { useLearningUserStore } from '@stores/user'
import learningApiUser from '@src/services/api/learning/user'
import DefineValidation from '@services/validation/DefineValidation'
import { Core as YubinBangoCore } from 'yubinbango-core2'
import dayjs from 'dayjs'

const router = useRouter()
const { setAccountInfo } = useLearningUserStore()
const { accountInfo } = storeToRefs(useLearningUserStore())

const modifiable = ref(true)

const { errors, meta, handleSubmit, handleReset, models } = new DefineValidation((modules) => {
  return {
    lastName: modules.string().required().min(1).max(100).label('氏名（姓）'),
    firstName: modules.string().required().min(1).max(100).label('氏名（名）'),
    lastNameKatakana: modules.string().required().min(1).max(100).katakana().fillWidth().label('フリガナ（セイ）'),
    firstNameKatakana: modules.string().required().min(1).max(100).katakana().fillWidth().label('フリガナ（メイ）'),
    // nickname: modules.string().required().fillWidth().label('ニックネーム'),
    year: modules.string().required().number().birthYear().label('生年月日（年）'),
    month: modules.string().required().number().birthMonth().label('生年月日（月）'),
    date: modules.string().required().number().birthDate().label('生年月日（日）'),
    gender: modules.string().required().label('性別'),
    pref: modules.string().required().label('都道府県'),
    office: modules.string().required().max(100).label('営業所会社名'),
    officeSection: modules.string().max(100).label('営業所屋号'),
    officeZipcode: modules.string().required().postalCode().label('会社郵便番号'),
    officePref: modules.string().required().label('会社都道府県'),
    officeCity: modules.string().required().max(10).label('会社市区町村'),
    officeStreet: modules.string().required().max(50).label('会社番地'),
    officeBuilding: modules.string().max(50).label('会社建物名（ビル名）'),
    officeTel: modules.string().max(20).required().phoneNumber().label('会社電話番号'),
    officeFax: modules.string().label('会社FAX番号'),
    officeManagerFlag: modules.string().label('販売責任者フラグ')
  }
})

const birthdayError = ref('')

// 生年月日プルダウン用
const years = [...Array(100)].map((_, i) => String(dayjs().year() - i)) // 120年前まで入力可能
const months = [...Array(12)].map((_, i) => String(i + 1))
const days = [...Array(31)].map((_, i) => String(i + 1))
const intl = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', { era: 'long' })

const optsYear = computed(() => {
  return years.map((y) => {
    const jpLabel = intl.format(dayjs(`${y}-01-01`).toDate()).split('/')[0]
    return { value: `${y}`, text: `${y}(${jpLabel})` }
  })
})

watch(() => models.year.value, () => {
  // console.log('models.year.value', models.year.value)
  if (birthdayValidate(models.year.value + '/' + models.month.value + '/' + models.date.value, 'YYYY/M/D')) {
    birthdayError.value = ''
  } else {
    birthdayError.value = '生年月日が正しくありません'
  }
})

watch(() => models.month.value, () => {
  // console.log('models.month.value', models.month.value)
  if (birthdayValidate(models.year.value + '/' + models.month.value + '/' + models.date.value, 'YYYY/M/D')) {
    birthdayError.value = ''
  } else {
    birthdayError.value = '生年月日が正しくありません'
  }
})

watch(() => models.date.value, () => {
  // console.log('models.date.value', models.date.value)
  if (birthdayValidate(models.year.value + '/' + models.month.value + '/' + models.date.value, 'YYYY/M/D')) {
    birthdayError.value = ''
  } else {
    birthdayError.value = '生年月日が正しくありません'
  }
})

// 生年月日が存在する日付かチェック
const birthdayValidate = (date, format) => {
  // console.log('date', date, 'format', format)
  return dayjs(date, format).format(format) === date;
}

const moveConfirmButtonDisabled = computed(() => {
  return !(meta.value.valid && meta.value.dirty)
})

// 郵便番号のフォーマット（ハイフンなし）
const formatZipcode = (value) => {
  return value.replace(/[^\d]/g, '').slice(0, 7)
}

// 郵便番号の入力監視
watch(() => models.officeZipcode.value, (newValue) => {
  models.officeZipcode.value = formatZipcode(newValue)
})

// 郵便番号
const searchAddress = () => {
  return new YubinBangoCore(models.officeZipcode.value, (value) => {
    console.log('value', value)
    models.officePref.value = value.region // 都道府県
    models.officeCity.value = value.locality // 市区町村
    models.officeStreet.value = value.street // 町域
  })
}

const moveConfirm = handleSubmit(async (values) => {
  console.log('models', models)
  let body = {
    lastName: models.lastName.value,
    firstName: models.firstName.value,
    lastNameKatakana: models.lastNameKatakana.value,
    firstNameKatakana: models.firstNameKatakana.value,
    // nickname: models.nickname.value,
    year: models.year.value,
    month: models.month.value,
    date: models.date.value,
    gender: models.gender.value,
    pref: models.pref.value,
    office: models.office.value,
    officeSection: models.officeSection.value,
    officeZipcode: models.officeZipcode.value,
    officePref: models.officePref.value,
    officeCity: models.officeCity.value,
    officeStreet: models.officeStreet.value,
    officeBuilding: models.officeBuilding.value,
    officeTel: models.officeTel.value,
    officeFax: models.officeFax.value,
    officeManagerFlag: models.officeManagerFlag.value
  }
  setAccountInfo(body)
  await router.push({
    name: 'AccountConfirm'
  })
})

onMounted (() => {
  window.scrollTo(0, 0)

  // 確認ページからの戻りであれば復元
  if (accountInfo.value.lastName) {
    models.lastName.value = accountInfo.value.lastName
    models.firstName.value = accountInfo.value.firstName
    models.lastNameKatakana.value = accountInfo.value.lastNameKatakana
    models.firstNameKatakana.value = accountInfo.value.firstNameKatakana
    // models.nickname.value = accountInfo.value.nickname
    models.year.value = accountInfo.value.year
    models.month.value = accountInfo.value.month
    models.date.value = accountInfo.value.date
    models.gender.value = accountInfo.value.gender
    models.pref.value = accountInfo.value.pref
    models.office.value = accountInfo.value.office
    models.officeSection.value = accountInfo.value.officeSection
    models.officeZipcode.value = accountInfo.value.officeZipcode
    models.officePref.value = accountInfo.value.officePref
    models.officeCity.value = accountInfo.value.officeCity
    models.officeStreet.value = accountInfo.value.officeStreet
    models.officeBuilding.value = accountInfo.value.officeBuilding
    models.officeTel.value = accountInfo.value.officeTel
    models.officeFax.value = accountInfo.value.officeFax
    models.officeManagerFlag.value = accountInfo.value.officeManagerFlag
  } else {
    // ユーザ情報取得
    learningApiUser.getUser().then((res) => {
      console.log('getUser res', res.data)
      models.lastName.value = res.data.displays.name[0]
      models.firstName.value = res.data.displays.name[1]
      models.lastNameKatakana.value = res.data.displays.spell[0]
      models.firstNameKatakana.value = res.data.displays.spell[1]
      // models.nickname.value = res.data.name
      models.year.value = res.data.displays.birthday.split('-')[0]
      models.month.value = parseInt(res.data.displays.birthday.split('-')[1], 10)
      models.date.value = parseInt(res.data.displays.birthday.split('-')[2], 10)
      models.gender.value = res.data.displays.gender
      models.pref.value = res.data.displays.address.pref
      models.office.value = res.data.displays.office.name
      models.officeSection.value = res.data.displays.office.section ? res.data.displays.office.section : ''
      models.officeZipcode.value = res.data.displays.office.address.zipcode
      models.officePref.value = res.data.displays.office.address.pref
      models.officeCity.value = res.data.displays.office.address.city
      models.officeStreet.value = res.data.displays.office.address.street
      models.officeBuilding.value = res.data.displays.office.address.building ? res.data.displays.office.address.building : ''
      models.officeTel.value = res.data.displays.office.telephone
      models.officeFax.value = res.data.displays.office.facsimile ? res.data.displays.office.facsimile : ''
      models.officeManagerFlag.value = res.data.displays.office.manager

      // flagsにnotModifiableがあれば編集不可
      if (res.data.flags && res.data.flags.includes('notModifiable')) {
        modifiable.value = false
      }
    }).catch((err) => {
      console.error('login err', err)
    })
  }
})
</script>

<template>
  <section>
    <div class="container">
      <h2>登録情報の確認・変更</h2>
      <p class="topMessage">
        登録情報を変更する場合は変更後、確認ボタンを押してください。
        <span
          v-if="!modifiable"
          class="attention"
        >
          登録情報の変更ができないアカウントです。
        </span>
      </p>
      <form @submit.prevent="sendAccountForm">
        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>氏名</label>
          <input
            id="password"
            v-model="models.lastName.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="山田"
            autocomplete="off"
            maxlength="256"
          ><input
            id="password"
            v-model="models.firstName.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="太郎"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.lastName"
            class="c-validationError"
          >{{ errors.lastName }}</span>
          <span
            v-if="errors.firstName"
            class="c-validationError"
          >{{ errors.firstName }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>フリガナ</label>
          <input
            id="password"
            v-model="models.lastNameKatakana.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="ヤマダ"
            autocomplete="off"
            maxlength="256"
          ><input
            id="password"
            v-model="models.firstNameKatakana.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text c-input-text--separate"
            placeholder="タロウ"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.lastNameKatakana"
            class="c-validationError"
          >{{ errors.lastNameKatakana }}</span>
          <span
            v-if="errors.firstNameKatakana"
            class="c-validationError"
          >{{ errors.firstNameKatakana }}</span>
        </div>

        <!-- <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>ニックネーム</label>
          <input
            v-model="models.nickname.value"
            type="text"
            class="c-input-text"
            placeholder="ヤマダタロウ"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.nickname"
            class="c-validationError"
          >{{ errors.nickname }}</span>
        </div> -->

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>生年月日</label>
          <div class="c-input-birthday-box">
            <select
              v-model="models.year.value"
              :disabled="!modifiable"
              class="c-input-select"
            >
              <option
                v-for="(item, index) in optsYear"
                :key="index"
                :selected="index === 0"
                :value="item.value"
              >
                {{ item.value }}
              </option>
            </select>
            <span>年</span>

            <select
              v-model="models.month.value"
              :disabled="!modifiable"
              class="c-input-select"
            >
              <option
                v-for="(item, index) in months"
                :key="index"
                :selected="index === 0"
                :value="item"
              >
                {{ item }}
              </option>
            </select>
            <span>月</span>

            <select
              v-model="models.date.value"
              :disabled="!modifiable"
              class="c-input-select"
            >
              <option
                v-for="(item, index) in days"
                :key="index"
                :selected="index === 0"
              >
                {{ item }}
              </option>
            </select>
            <span>日</span>
          </div>
          <span
            v-if="errors.year"
            class="c-validationError"
          >{{ errors.year }}</span>
          <span
            v-if="errors.month"
            class="c-validationError"
          >{{ errors.month }}</span>
          <span
            v-if="errors.date"
            class="c-validationError"
          >{{ errors.date }}</span>
          <span
            v-if="birthdayError"
            class="c-validationError"
          >{{ birthdayError }}</span>
        </div>

        <div class="p-form-field p-form-field--required">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>性別</label>
          <div class="p-filter-items__list">
            <div
              v-for="item, index in GENDER_OPTIONS"
              :key="index"
              class="c-checkbox-switch p-filter-items__item"
            >
              <label class="c-checkbox-switch__label">
                <input
                  v-model="models.gender.value"
                  :disabled="!modifiable"
                  type="radio"
                  name="gender"
                  :value="item.value"
                >
                <span>{{ item.name }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>自宅都道府県</label>
          <select
            v-model="models.pref.value"
            :disabled="!modifiable"
            class="c-input-select"
          >
            <option value="">
              未選択
            </option>
            <option
              v-for="(item, index) in PREF_OPTIONS"
              :key="index"
              :value="item.value"
              :selected="item.value === models.pref.value"
            >
              {{ item.name }}
            </option>
          </select>
          <span
            v-if="errors.pref"
            class="c-validationError"
          >{{ errors.pref }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>営業所会社名</label>
          <input
            v-model="models.office.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.office"
            class="c-validationError"
          >{{ errors.office }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          >営業所屋号</label>
          <input
            v-model="models.officeSection.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.officeSection"
            class="c-validationError"
          >{{ errors.officeSection }}</span>
        </div>

        <div class="p-form-field zipcode">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>会社住所</label>
          <input
            v-model="models.officeZipcode.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder="郵便番号"
            autocomplete="off"
            maxlength="7"
          >

          <button
            class="zipCodeBtn"
            @click.prevent="searchAddress()"
          >
            検索
          </button>

          <select
            v-model="models.officePref.value"
            :disabled="!modifiable"
            class="c-input-select"
          >
            <option value="">
              都道府県
            </option>
            <option
              v-for="(item, index) in PREF_OPTIONS"
              :key="index"
              :value="item.value"
              :selected="item.value === models.pref.value"
            >
              {{ item.name }}
            </option>
          </select>
          <span
            v-if="errors.officeZipcode"
            class="c-validationError"
          >{{ errors.officeZipcode }}</span>
          <span
            v-if="errors.officePref"
            class="c-validationError"
          >{{ errors.officePref }}</span>
        </div>

        <div class="p-form-field">
          <input
            v-model="models.officeCity.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder="市区町村"
            autocomplete="off"
            maxlength="128"
          >
          <span
            v-if="errors.officeCity"
            class="c-validationError"
          >{{ errors.officeCity }}</span>
        </div>

        <div class="p-form-field">
          <input
            v-model="models.officeStreet.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder="番地"
            autocomplete="off"
            maxlength="128"
          >
          <span
            v-if="errors.officeStreet"
            class="c-validationError"
          >{{ errors.officeStreet }}</span>
        </div>

        <div class="p-form-field">
          <input
            v-model="models.officeBuilding.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder="建物名（ビル名）"
            autocomplete="off"
            maxlength="256"
          >
          <span
            v-if="errors.officeBuilding"
            class="c-validationError"
          >{{ errors.officeBuilding }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>会社電話番号</label>
          <input
            v-model="models.officeTel.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="15"
          >
          <span
            v-if="errors.officeTel"
            class="c-validationError"
          >{{ errors.officeTel }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          >会社FAX番号</label>
          <input
            v-model="models.officeFax.value"
            :disabled="!modifiable"
            type="text"
            class="c-input-text"
            placeholder=""
            autocomplete="off"
            maxlength="15"
          >
          <span
            v-if="errors.officeFax"
            class="c-validationError"
          >{{ errors.officeFax }}</span>
        </div>

        <div class="p-form-field checkbox">
          <label
            class="c-input-label"
          >販売責任者</label>
          <div class="p-filter-items__list">
            <div class="c-checkbox-switch p-filter-items__item">
              <label class="c-checkbox-switch__label">
                <input
                  v-model="models.officeManagerFlag.value"
                  type="radio"
                  name="officeManagerFlag"
                  :value="true"
                >
                <span>はい</span>
              </label>
            </div>
            <div class="c-checkbox-switch p-filter-items__item">
              <label class="c-checkbox-switch__label">
                <input
                  v-model="models.officeManagerFlag.value"
                  type="radio"
                  name="officeManagerFlag"
                  :value="false"
                >
                <span>いいえ</span>
              </label>
            </div>
          </div>
        </div>

        <div class="btnContainer">
          <button
            class="c-button c-button--medium c-button--yellow"
            :disabled="moveConfirmButtonDisabled || !modifiable || birthdayError !== ''"
            @click="moveConfirm"
          >
            確認
          </button>
          <RouterLink
            :to="{name:'Dashboard'}"
            class="c-button c-button--medium c-button--border"
          >
            戻る
          </RouterLink>
        </div>
      </form>
    </div>
  </section>
</template>

<style scoped lang="scss">
.container {
  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }
}
.topMessage {
  margin-bottom: 20px;
  .attention {
    display: block;
    color: #F00;
  }
}
.c-input-select {
  width: 100px;
  height: 32px;
}
.zipcode {
  .c-input-text {
    width: 80px;
  }
  .zipCodeBtn {
    display: inline-block;
    width: 50px;
    height: 30px;
    margin: 0 15px 0 15px;
    border-radius: 4px;
    text-align: center;
    background: var(--corporate);
    color: #000;
    vertical-align: 6px;
  }
}
.p-form-field.checkbox {
  label {
    margin-right: 15px;
  }
  input[type="checkbox"] {
    appearance: auto;
    vertical-align: -1px;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}

.btnContainer {
  margin-top: 20px;
}
</style>
